import React, { useState, useEffect, useCallback } from 'react';
import { Plus } from 'lucide-react';

import { Button } from '@epoch-ui/components/Button/button';
import { Textarea } from '@epoch-ui/components/Textarea/textarea';
import { UserInvite } from 'types/invites.types';
import { GuestStatus } from 'types/guest.types';
import { inviteGuestsApi } from '@event-editor/api/invite-guests';
import { useEditableEvent } from '@event-editor/hooks/useEditableEvent';

import { useInviteGuestsModalContext } from '../../hooks/useInviteGuestsModalContext';

const LIMIT_PER_CALL = 500;

export const InviteEmailImporter: React.FC = () => {
  const event = useEditableEvent();
  const { addEmailImports, markEmailDedupeRequired } = useInviteGuestsModalContext();

  const [emailsString, setEmailsString] = useState('');
  const [emails, setEmails] = useState<string[]>([]);
  const [processingIndex, setProcessingIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Parse out email string by ',' or space
   */
  const handleImport = (): void => {
    // Parse emails
    const arrayOfEmails = emailsString
      .split(/\s*[\s,]\s*/)
      .map((email) => email.trim().toLowerCase())
      .filter((email) => email.length > 5);

    // Remove duplicates
    const uniqueArrayOfEmails = Array.from(new Set(arrayOfEmails));

    // Set current emails to process
    setEmails(uniqueArrayOfEmails);
    setEmailsString('');
  };

  const processUsersByEmailIncrementally = useCallback(async (): Promise<void> => {
    if (processingIndex >= emails.length) {
      setProcessingIndex(0);
      setEmails([]);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const emailBatch = emails.slice(processingIndex, processingIndex + LIMIT_PER_CALL);

      const invitableUsers = await inviteGuestsApi.getInvitableUsers({
        emails: emailBatch,
        eventId: event.id!
      });

      /**
       * 1. Loop through emails, check if email exists in returned data.
       * 2. If email is NOT found from returned data, mark it as 'invalid'.
       */
      const usersToInvite = emailBatch.reduce<UserInvite[]>((acc, email) => {
        const userToInvite = invitableUsers.find((user) => user.email === email);

        // 1. If we found user from 'invitableUsers'
        if (userToInvite) {
          // 1a. Skip users who already have a status with the event (except UNINVITED)
          if (
            userToInvite.currentStatus !== null &&
            userToInvite.currentStatus !== GuestStatus.UNINVITED
          ) {
            markEmailDedupeRequired();
            return acc;
          }

          // 1b. Add the user to the list
          acc.push(userToInvite);
          return acc;
        }

        // 2. Could not find email from our backend -> Mark it as `isInvalid`
        acc.push({ email, isInvalid: true });
        return acc;
      }, []);

      addEmailImports(usersToInvite);
      setProcessingIndex(processingIndex + LIMIT_PER_CALL);
      setIsLoading(false);
    } catch (error) {
      console.error('Error processing emails:', error);
      setIsLoading(false);
      setProcessingIndex(0);
      setEmails([]);
    }
  }, [emails.length, processingIndex, addEmailImports, markEmailDedupeRequired, event.id]);

  useEffect(() => {
    if (emails.length > 0) {
      processUsersByEmailIncrementally();
    }
  }, [emails, processUsersByEmailIncrementally]);

  useEffect(() => {
    if (processingIndex > 0 && processingIndex < emails.length) {
      processUsersByEmailIncrementally();
    }
  }, [processingIndex, emails.length, processUsersByEmailIncrementally]);

  return (
    <div className="tw-mt-4 tw-overflow-y-auto tw-pr-1">
      <h4 className="tw-mb-3 tw-text-sm tw-font-medium tw-leading-4">Enter emails</h4>

      <Textarea
        value={emailsString}
        onChange={(e) => setEmailsString(e.target.value)}
        className="tw-h-52 tw-max-h-60 tw-min-h-18 tw-w-full tw-resize-none"
        placeholder="Enter email addresses..."
      />

      <p className="tw-my-0 tw-mb-4 tw-mt-2 tw-text-xs tw-text-primary-grey">
        Separate email by spaces or commas
      </p>

      <Button
        onClick={handleImport}
        loading={isLoading}
        disabled={!emailsString}
        className="tw-gap-2"
      >
        <Plus className="tw-size-4" />
        Import
      </Button>
    </div>
  );
};
